import React, { useCallback, useEffect, useState } from "react";

import {
  getPlatforms,
  testPlatformConnection,
  getHubSpotHealth,
  syncAllHubSpotDeals,
  getAgencyTemplatesList,
  getPlatformSettings,
  savePlatformSettings,
  deletePlatformSettings,
  ApiError,
} from "../api.js";
import { getCurrentUser } from "./LoginPage.jsx";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const currentUser = getCurrentUser();
  const [platforms, setPlatforms] = useState([]);
  const [hubspotHealth, setHubspotHealth] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action states
  const [testingPlatform, setTestingPlatform] = useState(null);
  const [platformTestResult, setPlatformTestResult] = useState(null);
  const [syncingHubspot, setSyncingHubspot] = useState(false);
  const [hubspotSyncResult, setHubspotSyncResult] = useState(null);

  // API key management
  const [apiKey, setApiKey] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [apiUserId, setApiUserId] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKeyPrefix, setApiKeyPrefix] = useState(null);
  const [savingKey, setSavingKey] = useState(false);
  const [keyMsg, setKeyMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [platRes, hsRes, tplRes, settingsRes] = await Promise.all([
        getPlatforms().catch(() => []),
        getHubSpotHealth().catch((e) => ({ ok: false, error: e.message, configured: false })),
        getAgencyTemplatesList().catch(() => ({ data: [] })),
        getPlatformSettings(currentUser).catch(() => null),
      ]);
      setPlatforms(Array.isArray(platRes) ? platRes : (platRes?.data || []));
      setHubspotHealth(hsRes);
      setTemplates(tplRes?.data || []);
      if (settingsRes) {
        setApiKeyConfigured(settingsRes.configured || false);
        setApiKeyPrefix(settingsRes.apiKeyPrefix || null);
        setApiBaseUrl(settingsRes.baseUrl || "");
        setApiUserId(settingsRes.apiUserId || "");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  async function handleTestPlatform(platform) {
    setTestingPlatform(platform);
    setPlatformTestResult(null);
    try {
      const result = await testPlatformConnection(platform, currentUser);
      setPlatformTestResult({ platform, ...result });
    } catch (err) {
      setPlatformTestResult({
        platform,
        ok: false,
        message: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setTestingPlatform(null);
    }
  }

  async function handleSyncHubSpot() {
    setSyncingHubspot(true);
    setHubspotSyncResult(null);
    try {
      const result = await syncAllHubSpotDeals();
      setHubspotSyncResult({ success: true, ...result });
    } catch (err) {
      setHubspotSyncResult({
        success: false,
        error: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setSyncingHubspot(false);
    }
  }

  async function handleSaveApiKey(e) {
    e.preventDefault();
    setSavingKey(true);
    setKeyMsg(null);
    try {
      const body = { userId: currentUser, platform: "adgentek" };
      if (apiKey) body.apiKey = apiKey;
      if (apiBaseUrl) body.baseUrl = apiBaseUrl;
      if (apiUserId) body.apiUserId = apiUserId;
      const result = await savePlatformSettings(body);
      setApiKeyConfigured(result.configured);
      setApiKeyPrefix(result.apiKeyPrefix);
      setApiKey("");
      setKeyMsg({ kind: "ok", text: "API key saved successfully." });
      setTimeout(() => setKeyMsg(null), 4000);
    } catch (err) {
      setKeyMsg({ kind: "error", text: `Failed to save: ${err instanceof ApiError ? err.message : String(err)}` });
    } finally {
      setSavingKey(false);
    }
  }

  async function handleClearApiKey() {
    if (!window.confirm("Remove your stored Adgentek API key? Campaign generation will fall back to the server default.")) return;
    setSavingKey(true);
    setKeyMsg(null);
    try {
      await deletePlatformSettings(currentUser);
      setApiKeyConfigured(false);
      setApiKeyPrefix(null);
      setApiKey("");
      setKeyMsg({ kind: "ok", text: "API key removed." });
      setTimeout(() => setKeyMsg(null), 4000);
    } catch (err) {
      setKeyMsg({ kind: "error", text: `Failed to clear: ${err instanceof ApiError ? err.message : String(err)}` });
    } finally {
      setSavingKey(false);
    }
  }

  if (loading) {
    return <div className="settings-page container"><p>Loading settings...</p></div>;
  }

  return (
    <div className="settings-page container">
      <div className="page-header">
        <h1>Settings</h1>
        <div className="page-header-actions">
          <button type="button" className="btn" onClick={fetchAll}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Platform Connections */}
      <div className="settings-section">
        <h3 className="section-title">Platform Connections</h3>
        <p className="section-description">
          Ad platform integrations for campaign generation. The Adgentek advertiser-api is the
          primary target.
        </p>
        {platforms.length === 0 ? (
          <div className="settings-empty">No platforms configured.</div>
        ) : (
          <div className="settings-cards">
            {platforms.map((p) => {
              const testRes = platformTestResult?.platform === p.platform ? platformTestResult : null;
              return (
                <div key={p.platform} className="settings-card">
                  <div className="settings-card-header">
                    <span className="settings-card-title">{p.platform}</span>
                    <span className={`settings-status-badge ${p.configured ? "status-ok" : "status-warn"}`}>
                      {p.status === "ready" ? "Ready" : "Needs API Key"}
                    </span>
                  </div>
                  <div className="settings-card-body">
                    <div className="settings-card-row">
                      <span className="muted">Base URL</span>
                      <span className="mono">{p.baseUrl}</span>
                    </div>
                    <div className="settings-card-row">
                      <span className="muted">API Key</span>
                      <span>{p.configured ? "Configured" : "Not set"}</span>
                    </div>
                  </div>
                  <div className="settings-card-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => handleTestPlatform(p.platform)}
                      disabled={testingPlatform === p.platform}
                    >
                      {testingPlatform === p.platform ? "Testing..." : "Test Connection"}
                    </button>
                    {testRes && (
                      <span className={`test-result ${testRes.ok ? "test-ok" : "test-fail"}`}>
                        {testRes.ok ? "Connected" : "Failed"} - {testRes.message}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Adgentek API Key Management */}
      <div className="settings-section">
        <h3 className="section-title">Adgentek API Key</h3>
        <p className="section-description">
          Store your Adgentek advertiser API key for campaign generation. This key is stored
          per user and used when creating campaigns on api.adgentek.ai.
        </p>
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-title">Your API Key</span>
            <span className={`settings-status-badge ${apiKeyConfigured ? "status-ok" : "status-warn"}`}>
              {apiKeyConfigured ? `Configured (${apiKeyPrefix})` : "Not Set"}
            </span>
          </div>
          <div className="settings-card-body">
            {keyMsg && (
              <div className={`alert ${keyMsg.kind === "error" ? "alert-error" : "alert-ok"}`} style={{ marginBottom: "1rem" }}>
                {keyMsg.text}
              </div>
            )}
            <form onSubmit={handleSaveApiKey} className="settings-form">
              <label className="filter-field">
                <span>API Key</span>
                <input
                  type="password"
                  placeholder={apiKeyConfigured ? "Enter new key to replace" : "adg_..."}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="filter-field">
                <span>Base URL (optional)</span>
                <input
                  type="text"
                  placeholder="https://api.adgentek.ai/webapi/v1/advertiser"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                />
              </label>
              <label className="filter-field">
                <span>API User ID (optional)</span>
                <input
                  type="text"
                  placeholder="Your Adgentek user UUID"
                  value={apiUserId}
                  onChange={(e) => setApiUserId(e.target.value)}
                />
              </label>
              <div className="settings-card-actions" style={{ marginTop: "1rem" }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={savingKey || !apiKey}>
                  {savingKey ? "Saving..." : "Save Key"}
                </button>
                {apiKeyConfigured && (
                  <button type="button" className="btn btn-danger-outline btn-sm" onClick={handleClearApiKey} disabled={savingKey}>
                    Clear Key
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* HubSpot Integration */}
      <div className="settings-section">
        <h3 className="section-title">HubSpot Integration</h3>
        <p className="section-description">
          Sync closed-won deals from HubSpot into Longship as draft IOs. The webhook receiver
          also creates IOs automatically when deals move to closed-won.
        </p>
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-title">HubSpot CRM</span>
            <span className={`settings-status-badge ${hubspotHealth?.ok ? "status-ok" : (hubspotHealth?.configured ? "status-warn" : "status-off")}`}>
              {hubspotHealth?.ok ? "Connected" : (hubspotHealth?.configured ? "Error" : "Not Configured")}
            </span>
          </div>
          <div className="settings-card-body">
            {hubspotHealth?.ok ? (
              <div className="settings-card-row">
                <span className="muted">Pipelines</span>
                <span>{hubspotHealth.pipelines?.length || 0} found</span>
              </div>
            ) : (
              <>
                <div className="settings-card-row">
                  <span className="muted">Status</span>
                  <span>
                    {hubspotHealth?.configured
                      ? hubspotHealth?.error || "Credentials configured but the connection failed."
                      : "HUBSPOT_ACCESS_TOKEN not set in API .env"}
                  </span>
                </div>
                {hubspotHealth?.configured && (
                  <div className="settings-card-row">
                    <span className="muted">Hint</span>
                    <span className="muted">
                      A HubSpot OAuth refresh token needs HUBSPOT_CLIENT_ID and
                      HUBSPOT_CLIENT_SECRET to exchange for an access token. A private
                      app token needs no client credentials.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="settings-card-actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleSyncHubSpot}
              disabled={syncingHubspot || !hubspotHealth?.ok}
              title="Pull all closed-won deals from HubSpot as draft IOs"
            >
              {syncingHubspot ? "Syncing..." : "Sync All Deals"}
            </button>
            {hubspotSyncResult && (
              <span className={`test-result ${hubspotSyncResult.success ? "test-ok" : "test-fail"}`}>
                {hubspotSyncResult.success
                  ? `Synced ${hubspotSyncResult.synced || 0}, skipped ${hubspotSyncResult.skipped || 0} of ${hubspotSyncResult.totalDeals || 0} deals`
                  : `Error: ${hubspotSyncResult.error}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Agency Templates */}
      <div className="settings-section">
        <h3 className="section-title">Agency Templates</h3>
        <p className="section-description">
          Per-agency field mapping templates injected into the LLM extraction prompt. Templates
          improve extraction accuracy by providing field name hints specific to each agency's IO
          format.
        </p>
        {templates.length === 0 ? (
          <div className="settings-empty">
            No agency templates configured yet. The IAB standard template exists in the parser
            but has not been loaded into the database.
          </div>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Agency ID</th>
                  <th>Field Mappings</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id || t.agencyId} className="dashboard-row">
                    <td className="mono">{t.agencyId || t.id}</td>
                    <td className="muted">
                      {t.fieldMappings
                        ? `${Object.keys(t.fieldMappings).length} mappings`
                        : "—"}
                    </td>
                    <td className="muted">
                      {t.updatedAt
                        ? new Date(t.updatedAt).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}